<template>
  <nav
    ref="navEl"
    class="mobile-bottom-nav border-t bg-white d-flex align-stretch w-100"
  >
    <div class="mobile-bottom-nav__indicator" :style="indicatorStyle" />

    <button
      v-for="(item, index) in BOTTOM_NAV_ITEMS"
      :key="item.value"
      :ref="(el) => setItemEl(el, index)"
      type="button"
      class="mobile-bottom-nav__item flex-grow-1 d-flex flex-column align-center justify-center ga-1"
      :disabled="item.disabled"
      @click="onItemClick(item)"
    >
      <VIcon
        :icon="
          isItemActive(item) && item.activeIcon ? item.activeIcon : item.icon
        "
        size="22"
        :color="isItemActive(item) ? 'primary' : 'gray-400'"
      />
      <span
        class=" "
        :class="isItemActive(item) ? 'text-primary' : 'text-gray-400'"
      >
        {{ item.label }}
      </span>
    </button>
  </nav>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VIcon } from 'vuetify/components'
import {
  BOTTOM_NAV_ITEMS,
  type BottomNavItem,
  type BottomNavItemValue,
} from '@/modules/Core/constants/bottomNav'

defineOptions({
  name: 'TheBottomNavigation',
})

const INDICATOR_WIDTH = 48

const route = useRoute()
const router = useRouter()

const navEl = ref<HTMLElement | null>(null)
const itemEls = ref<(HTMLElement | null)[]>([])
const indicatorOffset = ref(0)

const activeTab = computed((): BottomNavItemValue => {
  const matched = BOTTOM_NAV_ITEMS.find((item) => isItemRouteActive(item))

  return matched?.value ?? 'more'
})

const activeIndex = computed(() =>
  BOTTOM_NAV_ITEMS.findIndex((item) => item.value === activeTab.value)
)

const indicatorStyle = computed(() => ({
  width: `${INDICATOR_WIDTH}px`,
  transform: `translate3d(${indicatorOffset.value}px, 0, 0)`,
}))

watch([activeIndex, () => route.path], () => {
  void updateIndicatorPosition()
})

onMounted(() => {
  void updateIndicatorPosition()
  window.addEventListener('resize', onViewportChange)
})

onUnmounted(() => {
  window.removeEventListener('resize', onViewportChange)
})

function setItemEl(el: unknown, index: number) {
  itemEls.value[index] = el instanceof HTMLElement ? el : null
}

function isItemRouteActive(item: BottomNavItem): boolean {
  if (!item.link) {
    return false
  }

  if (item.link === '/') {
    return route.path === '/'
  }

  return route.path === item.link || route.path.startsWith(`${item.link}/`)
}

function isItemActive(item: BottomNavItem): boolean {
  return item.value === activeTab.value
}

function updateIndicatorPosition() {
  return nextTick(() => {
    const nav = navEl.value
    const item = itemEls.value[activeIndex.value]

    if (!nav || !item || activeIndex.value < 0) {
      return
    }

    const navRect = nav.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()

    indicatorOffset.value =
      itemRect.left - navRect.left + (itemRect.width - INDICATOR_WIDTH) / 2
  })
}

function onViewportChange() {
  void updateIndicatorPosition()
}

function onItemClick(item: BottomNavItem) {
  if (item.disabled) {
    return
  }

  if (item.link && route.path !== item.link) {
    router.push(item.link)
  }
}
</script>

<style scoped>
.mobile-bottom-nav {
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  width: 100%;
  z-index: 1004;
  height: 66px;
}

.mobile-bottom-nav__indicator {
  position: absolute;
  top: 0;
  left: 0;
  height: 4px;
  border-radius: 0 0 999px 999px;
  background: rgb(var(--v-theme-primary));
  transition: transform 0.25s ease;
  pointer-events: none;
  will-change: transform;
}

.mobile-bottom-nav__item {
  min-width: 0;
  border: 0;
  background: transparent;
  letter-spacing: 0;
  cursor: pointer;
}

.mobile-bottom-nav__item:disabled {
  opacity: 0.45;
  cursor: default;
}
</style>
